/**
 * useWorkplanJob hook for managing workplan generation
 * Uses the generic report job infrastructure (like proposals do)
 */

import { useState, useCallback, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { 
  submitReportJob,
  getReportJobStatus,
} from '../api/reports';
import { ReportJobStatus } from '../types/api';
import { useToastStore } from '../stores/toast';

export interface WorkplanTemplate {
  sections: Array<{ name: string; description: string }>;
  default_questions: Array<{ section: string; question: string }>;
  template_source: string;
  service_line?: string;
  confidence_score: number;
  entity_types_by_question?: Record<string, string[]>;
}

/**
 * Hook for submitting workplan generation jobs
 * Wraps generic submitReportJob with workplan-specific configuration
 */
export function useWorkplanJobSubmit() {
  const { addToast } = useToastStore();

  return useMutation<ReportJobStatus, Error, {
    pastProjectId: string;
    projectId: string;
    clientName: string;
    questions: Array<{ section: string; question: string }>;
    metadata?: {
      workplan_name?: string;
      service_line?: string;
      include_past_workplans?: boolean;
      include_evidence?: boolean;
      template_source?: string;
    };
  }>({
    mutationFn: async (params) => {
      // Use generic report job infrastructure with workplan_generation report type
      return submitReportJob({
        project_id: params.projectId,
        report_type: 'workplan_generation',  // Use the new report type we added to enum
        client_name: params.clientName,
        questions: params.questions,
        metadata: {
          ...params.metadata,
          past_project_id: params.pastProjectId,
          report_type_override: 'workplan_generation',  // Explicit override for clarity
        },
        enable_cache: true,
      });
    },
    onSuccess: () => {
      addToast({
        type: 'success',
        title: 'Workplan job submitted',
        message: 'Workplan generation has started',
      });
    },
    onError: (error) => {
      addToast({
        type: 'error',
        title: 'Failed to submit workplan job',
        message: error.message,
      });
    },
  });
}

/**
 * Hook for monitoring workplan job status
 * Uses generic report job status endpoint
 */
export function useWorkplanJobStatus(jobId: string | null, enabled: boolean = true) {
  return useQuery<ReportJobStatus, Error>({
    queryKey: ['workplan-job', jobId],
    queryFn: () => getReportJobStatus(jobId!),
    enabled: enabled && !!jobId,
    refetchInterval: (query) => {
      // Poll while job is running
      const data = query.state.data;
      if (data && ['pending', 'queued', 'running', 'checkpointed'].includes(data.status)) {
        return 3000; // Poll every 3 seconds
      }
      // When completed, do one final refetch to ensure we have the latest data
      if (data && data.status === 'completed') {
        setTimeout(() => {
          query.refetch();
        }, 1000);
      }
      return false; // Stop polling when complete/failed
    },
  });
}
